// Medication helpers shared by the overview and the new-visit form.
import type { PetRecord } from "../shared/types";

const NSAIDS = /carprofen|meloxicam|deracoxib|firocoxib|grapiprant|robenacoxib|ketoprofen|aspirin|piroxicam|etodolac|rimadyl|metacam/i;

/** Why a medication needs a second look for this pet (drug allergy, or an NSAID with an NSAID sensitivity). */
export function medCaution(name: string, allergies: PetRecord["allergies"]): string | null {
  const n = name.toLowerCase();
  const allergy = allergies.drug.find((a) => n.includes(a.toLowerCase().split(/[\s-]/)[0]));
  if (allergy) return `Drug allergy: ${allergy}`;
  const nsaid = [...allergies.medical, ...allergies.drug].find((a) => /nsaid/i.test(a));
  if (nsaid && NSAIDS.test(n)) return nsaid;
  return null;
}

export interface MedRow { key: string; name: string; detail: string; date: string | null; month?: boolean; source: string | null; caution: string | null }

/** Everything the pet is currently taking: clinic prescriptions, other-source medication, prescription diets. */
export function currentMedications(r: PetRecord): MedRow[] {
  return [
    ...r.prescriptions.active
      .filter((p) => p.category === "medication" || p.category === "topical")
      .map((p) => ({ key: `rx${p.id}`, name: p.name, detail: p.instructions, date: p.startedOn, source: null })),
    ...r.externalMeds.map((m) => ({
      key: `ext-${m.name}`, name: m.name,
      detail: [m.instructions, m.quantity && `Qty ${m.quantity}`].filter(Boolean).join(" · "),
      date: m.startedOn ?? m.reportedOn, month: true, source: m.source === "owner" ? "owner" : "other clinic",
    })),
    ...r.prescriptions.diet.map((p) => ({ key: `diet${p.id}`, name: p.name, detail: p.instructions, date: p.lastSupplied ?? p.startedOn, month: true, source: null })),
  ].map((m) => ({ ...m, caution: medCaution(m.name, r.allergies) }));
}
